import { StandaloneSearchBox } from '@react-google-maps/api';
import { StoreLocatorActionTypes } from '@state/features/common.types';
import { setAddress } from '@state/features/storelocator.slice';
import analytics from '@utils/analytics';
import classNames from 'classnames';
import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import whatInput from 'what-input';
import StoreLocatorFilters from './StoreLocatorFilters';
import StoreLocatorGeo from './StoreLocatorGeo';
import StoreLocatorRegions from './StoreLocatorRegions';
import StoreLocatorResults from './StoreLocatorResults';
import { autoChooseClassnames, autoChooseProps, getId } from '@common/utils/brandDiffsUtils';
import { useElementSize } from 'usehooks-ts';
const StoreLocatorSearch = (props) => {
    const { t } = useTranslation();
    const { filteredStores } = props;
    const dispatch = useDispatch();
    const { stores, address, filters, zoom } = useSelector((state) => state.storelocator);
    const initalSearchValue = props.initialSearch ? props.initialSearch : address;
    const inputRef = useRef(null);
    const showResultslistRef = useRef(null);
    const [showResultslist, setShowResultslist] = useState(false);
    const [showRegions, setShowRegions] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [inputFocus, setInputFocus] = useState(false);
    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };
    const [filtersRef, { width }] = useElementSize();
    const placeService = useMemo(() => new google.maps.places.PlacesService(document.createElement('div')), []);
    const [focusType, setFocusType] = useState('keyboard');
    const handleFocus = () => {
        if (whatInput.ask() === 'keyboard') {
            setFocusType('keyboard');
        }
        else {
            setFocusType('mouse');
        }
    };
    useEffect(() => {
        // siamo nella city o country page
        // mostra la lista per avere gli url in pagina x SEO internal linking
        if (props.initialSearch) {
            setShowResultslist(true);
        }
    }, []);
    useLayoutEffect(() => {
        // Set a CSS variable used by input to make SI input layout responsive
        if (getId() === 'ST') {
            document.documentElement.style.setProperty(`--storelocator-filters-width`, `${width / 10}rem`);
        }
    }, [width]);
    const onLoad = (autocomplete) => {
        inputRef.current = autocomplete;
    };
    const onPlaceChanged = async () => {
        var _a, _b, _c;
        if (inputRef.current) {
            const selectedPlaces = inputRef.current.getPlaces();
            if (selectedPlaces.length == 0) {
                dispatch(setAddress(undefined));
                return;
            }
            const bounds = new google.maps.LatLngBounds();
            selectedPlaces.forEach((place) => {
                if (!place.geometry || !place.geometry.location) {
                    console.log('Returned place contains no geometry');
                    return;
                }
                if (place.geometry.viewport) {
                    // Only geocodes have viewport.
                    bounds.union(place.geometry.viewport);
                }
                else {
                    bounds.extend(place.geometry.location);
                }
            });
            if (!!selectedPlaces[0].formatted_address) {
                analytics.storelocatorSearch(selectedPlaces[0].formatted_address);
                if (selectedPlaces[0].name === '') {
                    dispatch(setAddress(undefined));
                }
                else {
                    if (selectedPlaces[0].address_components) {
                        // se è una città
                        // props.searchedCity(undefined);
                        // if (selectedPlaces[0].address_components[0]?.types.includes('locality')) {
                        //   props.searchedCity(selectedPlaces[0].address_components[0]?.short_name);
                        // }
                        // se è una country
                        props.searchedCountry(undefined);
                        if ((_a = selectedPlaces[0].address_components[0]) === null || _a === void 0 ? void 0 : _a.types.includes('country')) {
                            props.searchedCountry((_b = selectedPlaces[0].address_components[0]) === null || _b === void 0 ? void 0 : _b.short_name);
                        }
                    }
                    else if ((_c = selectedPlaces[0].types) === null || _c === void 0 ? void 0 : _c.includes('country')) {
                        await new Promise((resolve) => {
                            placeService.getDetails(selectedPlaces[0], (result, status) => {
                                var _a;
                                if (status === google.maps.places.PlacesServiceStatus.OK) {
                                    console.log('result ', result);
                                    props.searchedCountry((_a = result.address_components[0]) === null || _a === void 0 ? void 0 : _a.short_name);
                                }
                                resolve(status);
                            });
                        });
                    }
                    dispatch(setAddress(selectedPlaces[0].formatted_address));
                    dispatch({ type: StoreLocatorActionTypes.UPDATE_BOUNDS, payload: { newBounds: bounds.toJSON(), fromSearch: true } });
                    setTimeout(() => {
                        var _a;
                        (_a = showResultslistRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    }, 2000);
                }
            }
        }
        else {
            console.log('Autocomplete is not loaded yet!');
        }
    };
    return (React.createElement("div", { className: "container container--search" },
        React.createElement("div", { className: "row justify-center" },
            React.createElement("div", { className: autoChooseClassnames({
                    /* eslint-disable  prettier/prettier */
                    MO: "col-12 col-sm-10 col-md-8 col-lg-6",
                    ST: "col-24 col-lg-20 col-xxl-12"
                }) },
                React.createElement("div", { role: "search", className: "search-store" },
                    React.createElement("div", { 
                        // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
                        role: "combobox", "aria-haspopup": "grid", "aria-expanded": "false", className: classNames('search-store__autocomplete', { 'show-list': showResultslist }) },
                        React.createElement(StandaloneSearchBox, { onLoad: onLoad, onPlacesChanged: onPlaceChanged },
                            React.createElement(React.Fragment, null,
                                React.createElement("input", { type: "text", defaultValue: initalSearchValue, placeholder: t('storelocator.field.label'), "aria-label": t('storelocator.field.label'), autoFocus: !props.initialSearch, autoComplete: "off", "aria-autocomplete": "list", onFocus: () => {
                                        setShowFilters(false);
                                        setShowResultslist(false);
                                        setShowRegions(false);
                                        getId() === 'ST' ? setInputFocus(true) : handleFocus();
                                    }, onBlur: () => getId() === 'ST' ? setInputFocus(false) : undefined, className: focusType === 'keyboard' ? 'pac-target-input' : 'no-outline pac-target-input' }))),
                        getId() === 'ST' && (React.createElement("span", { className: classNames('search-store__autocomplete--after', { focus: inputFocus }) }))),
                    React.createElement("div", { className: "search-store__results-filters", ref: filtersRef },
                        React.createElement("div", { className: "search-store__resultslabel" }, filteredStores.length ? (address || filters.collections.length > 0 || filters.services.length > 0 || zoom > 5 || initalSearchValue ? (React.createElement("button", { type: "button", "aria-controls": "resultsOptions", className: "alllocations__toggle", onClick: () => {
                                setShowFilters(false);
                                setShowRegions(false);
                                setShowResultslist(true);
                            }, "aria-expanded": showResultslist ? 'true' : 'false', "aria-haspopup": "dialog", ref: showResultslistRef }, t('storelocator.nboutiques', { 0: filteredStores.length }))) : (React.createElement("button", { type: "button", "aria-controls": "allLocationsOptions", className: "alllocations__toggle allocations__toggle--nosearch", onClick: () => {
                                setShowFilters(false);
                                setShowResultslist(false);
                                setShowRegions(true);
                            }, "aria-expanded": showRegions ? 'true' : 'false', "aria-haspopup": "dialog" }, autoChooseProps({
                            MO: t('storelocator.allnboutiques', { 0: stores.length }),
                            ST: t('storelocator.allboutiques')
                        })))) : (React.createElement("i", null, t('storelocator.noresults')))),
                        showResultslist ? React.createElement(StoreLocatorResults, { filteredStores: filteredStores, close: () => setShowResultslist(false) }) : React.createElement("div", { id: "resultsOptions" }),
                        showRegions ? (React.createElement(StoreLocatorRegions, { placeService: placeService, filteredStores: stores, close: (selectedCountry) => {
                                setShowRegions(false);
                                if (selectedCountry && whatInput.ask() == 'keyboard') {
                                    setShowResultslist(true);
                                }
                            } })) : (React.createElement("div", { id: "allLocationsOptions" })),
                        getId() === 'MO' && React.createElement(StoreLocatorGeo, null),
                        React.createElement("button", { className: "refine__toggle", type: "button", "aria-expanded": showFilters, "aria-controls": "refineOptions", onClick: () => {
                                if (!showFilters) {
                                    analytics.storelocatorOpenRefine();
                                }
                                toggleFilters();
                            } }, t('refine.title')),
                        showFilters ? React.createElement(StoreLocatorFilters, { close: toggleFilters }) : React.createElement("div", { id: "refineOptions" })))))));
};
export default StoreLocatorSearch;
